import { CertificationRecord } from "./certificateTypes";
import { CompanyBundleType } from "./certificateBundles";

export type CompanyCertificationRecord = CertificationRecord & {
  bundleTypes: CompanyBundleType[];
};

type CompanyCertInput = Omit<CertificationRecord, "scope" | "certificateType"> & {
  certificateType?: "readiness";
  bundleTypes: CompanyBundleType[];
};

const company = (cert: CompanyCertInput): CompanyCertificationRecord => ({
  ...cert,
  scope: "company",
  certificateType: cert.certificateType ?? "readiness",
});

/**
 * Company readiness certifications — sales collateral names/taglines.
 * Gov: FCA Protection Program · Public: SEC Investor Mitigation · Private: all others.
 */
export const COMPANY_CERTIFICATIONS: CompanyCertificationRecord[] = [
  company({
    id: "company-archie-securepath",
    bundleTypes: ["private"],
    title: "Archie+ SecurePath",
    subtitle: "REAL PERSON, REAL TIME, REAL PROTECTION.",
    description:
      "Company readiness for Archie+ SecurePath — real person, real time route protection with check-ins, zone alerts, and escalation for the workforce.",
    recognitionText:
      "for achieving company readiness in Archie+ SecurePath with documented workforce protection deployment.",
    themeColor: "#A974FF",
    emoji: "🛡️",
    iconSuggestion: "Shield with route path",
    onmywayFeatures: ["Archie+ SecurePath™", "Safe-route guidance", "911 escalation"],
    criteria: [
      "Enable Archie+ SecurePath for assigned workforce",
      "Document check-in and escalation configuration",
      "Maintain readiness records in OnMyWay One",
    ],
    issueDate: "Upon program validation",
    expirationDate: "12 months from issue date",
  }),
  company({
    id: "company-workplace-emergency-dispatch",
    bundleTypes: ["private"],
    title: "Workplace Emergency Dispatch",
    subtitle: "QUICK RESPONSE WHEN EMPLOYEES NEED HELP.",
    description:
      "Company readiness for workplace emergency dispatch — quick response protocols when employees need help.",
    recognitionText:
      "for achieving company readiness in Workplace Emergency Dispatch with documented emergency response procedures.",
    themeColor: "#DC2626",
    emoji: "🚨",
    iconSuggestion: "Emergency dispatch",
    onmywayFeatures: ["Workplace Emergency Program™", "Panic workflows", "911 routing"],
    criteria: [
      "Deploy workplace emergency dispatch workflows",
      "Train workforce on emergency response",
      "Retain incident and dispatch records",
    ],
    issueDate: "Upon program validation",
    expirationDate: "12 months from issue date",
  }),
  company({
    id: "company-ridewatch-travel-protection",
    bundleTypes: ["private"],
    title: "RideWatch / Travel Protection Program",
    subtitle:
      "MONITORED WITH REAL PEOPLE, DOCUMENTED, AND BACKED BY SAFETY PROTOCOLS.",
    description:
      "Company readiness for rideshare and travel protection — monitored trips documented with safety protocols.",
    recognitionText:
      "for achieving company readiness in the Rideshare RideWatch / Travel Protection Program.",
    themeColor: "#3FA9FF",
    emoji: "🚕",
    iconSuggestion: "Monitored vehicle",
    onmywayFeatures: ["Rideshare RideWatch™", "Travel protection", "Evidence capture"],
    criteria: [
      "Enable travel protection for business travel workforce",
      "Document ride monitoring procedures",
      "Maintain travel safety audit records",
    ],
    issueDate: "Upon program validation",
    expirationDate: "12 months from issue date",
  }),
  company({
    id: "company-mental-health-shield",
    bundleTypes: ["private"],
    title: "Mental Health Shield Program",
    subtitle: "SUPPORT WHEN STRESS OR CRISIS REQUIRES HELP OR IMMEDIATE DISPATCH.",
    description:
      "Company readiness for mental health shield support when stress or crisis requires help or immediate dispatch.",
    recognitionText:
      "for achieving company readiness in the Mental Health Shield Program with documented wellness escalation pathways.",
    themeColor: "#E5484D",
    emoji: "❤️",
    iconSuggestion: "Heart shield",
    onmywayFeatures: ["Mental Health Shield™", "Crisis dispatch", "Confidential escalation"],
    criteria: [
      "Deploy mental health shield resources to workforce",
      "Document escalation and support pathways",
      "Maintain program participation records",
    ],
    issueDate: "Upon program validation",
    expirationDate: "12 months from issue date",
  }),
  company({
    id: "company-employee-violence-response",
    bundleTypes: ["private"],
    title: "Employee Violence Response System",
    subtitle: "FAST, SAFE RESPONSE TO THREATENING SITUATIONS.",
    description:
      "Company readiness for employee violence response — fast, safe response to threatening situations.",
    recognitionText:
      "for achieving company readiness in the Employee Violence Response System with documented threat response procedures.",
    themeColor: "#B91C1C",
    emoji: "🛡️",
    iconSuggestion: "Violence response shield",
    onmywayFeatures: ["Threat response", "Security escalation", "Evidence documentation"],
    criteria: [
      "Establish violence response protocols",
      "Train designated responders and workforce",
      "Retain threat incident documentation",
    ],
    issueDate: "Upon program validation",
    expirationDate: "12 months from issue date",
  }),
  company({
    id: "company-lone-worker-lifeline",
    bundleTypes: ["private"],
    title: "Remote / Lone Worker Lifeline",
    subtitle: "PROTECTION FOR SALES / REMOTE WORKERS AND EMPLOYEES WORKING ALONE.",
    description:
      "Company readiness for remote and lone worker protection — sales, remote, and isolated workforce coverage.",
    recognitionText:
      "for achieving company readiness in Remote / Lone Worker Lifeline with documented check-in and SOS workflows.",
    themeColor: "#FF8A3D",
    emoji: "👤",
    iconSuggestion: "Lone worker check-in",
    onmywayFeatures: ["Remote-Worker Lifeline™", "SOS timers", "Location-aware workflows"],
    criteria: [
      "Enable lone worker protection for at-risk roles",
      "Document check-in and SOS procedures",
      "Maintain lone worker safety records",
    ],
    issueDate: "Upon program validation",
    expirationDate: "12 months from issue date",
  }),
  company({
    id: "company-safety-in-motion",
    bundleTypes: ["private"],
    title: "Safety In-Motion Program",
    subtitle: "PROTECTION FROM SLIPS AND FALLS, AI + REAL PERSON DISPATCH",
    description:
      "Company readiness for slips, falls, and in-motion hazards with AI detection and real person dispatch.",
    recognitionText:
      "for achieving company readiness in the Safety In-Motion Program with documented fall prevention and response.",
    themeColor: "#7CC242",
    emoji: "👷",
    iconSuggestion: "Hard hat with mobile report",
    onmywayFeatures: ["Safety In-Motion™", "Fall detection", "OSHA incident trails"],
    criteria: [
      "Deploy safety in-motion monitoring for mobile workforce",
      "Document hazard reporting procedures",
      "Retain incident and prevention records",
    ],
    issueDate: "Upon program validation",
    expirationDate: "12 months from issue date",
  }),
  company({
    id: "company-onmyway-drive",
    bundleTypes: ["private"],
    title: "OnMyWay Drive Program",
    subtitle:
      "DISTRACTED DRIVING PROTECTION FOR ALL EMPLOYEES ANYTIME THEY GET BEHIND THE WHEEL.",
    description:
      "Company readiness for distracted driving protection whenever employees drive for work or personal use.",
    recognitionText:
      "for achieving company readiness in the OnMyWay Drive Program with documented safe driving compliance.",
    themeColor: "#3B82F6",
    emoji: "🚗",
    iconSuggestion: "Vehicle with shield",
    onmywayFeatures: ["OnMyWay Drive™", "Drive mode", "Safe driving documentation"],
    criteria: [
      "Enable OnMyWay Drive for driving workforce",
      "Document distracted driving policy adherence",
      "Maintain driving safety participation records",
    ],
    issueDate: "Upon program validation",
    expirationDate: "12 months from issue date",
  }),
  company({
    id: "company-onmyway-one-reporting",
    bundleTypes: ["private"],
    title: "OnMyWay One – Reporting and Repository",
    subtitle: "SIMPLE REPORTING / SAFETY PROGRAM AND COMPLIANCE CERTIFICATION.",
    description:
      "Company readiness for OnMyWay One reporting — safety program and compliance repository.",
    recognitionText:
      "for maintaining active OnMyWay One reporting and repository with documented compliance records.",
    themeColor: "#38BDF8",
    emoji: "📅",
    iconSuggestion: "Reporting repository",
    onmywayFeatures: [
      "OnMyWay One reporting",
      "Certification repository",
      "Compliance dashboards",
    ],
    criteria: [
      "Activate OnMyWay One reporting program",
      "Generate and retain compliance reports",
      "Maintain certification repository records",
    ],
    issueDate: "Upon reporting program activation",
    expirationDate: "Ongoing — reviewed quarterly",
  }),
  company({
    id: "company-verifyai-cybersecurity",
    bundleTypes: ["private"],
    title: "VerifyAI Cybersecurity",
    subtitle:
      "IDENTIFY MANIPULATED MEDIA AND DIGITAL IMPERSONATION AND RISKS THAT ARE ATTACHED.",
    description:
      "Company readiness for VerifyAI cybersecurity — identifying manipulated media, impersonation, and digital risks.",
    recognitionText:
      "for achieving company readiness in VerifyAI Cybersecurity with documented identity verification procedures.",
    themeColor: "#2BD4C8",
    emoji: "🔒",
    iconSuggestion: "Identity scan shield",
    onmywayFeatures: ["VerifyAI™", "Deepfake detection", "Impersonation prevention"],
    criteria: [
      "Deploy VerifyAI for at-risk workforce segments",
      "Document verification and fraud prevention procedures",
      "Maintain cybersecurity readiness records",
    ],
    issueDate: "Upon program validation",
    expirationDate: "12 months from issue date",
  }),
  company({
    id: "fca-protection-program-govcon",
    bundleTypes: ["government_contractor"],
    title: "FCA Protection Program",
    subtitle: "COMPLIANT / EMPLOYEE SAFETY PROOF",
    description:
      "Government contractor company readiness — FCA-aligned employee safety proof and compliance documentation for federal contractors.",
    recognitionText:
      "for achieving FCA Protection Program readiness with documented employee safety proof and compliance records.",
    themeColor: "#7C3AED",
    emoji: "🏛️",
    iconSuggestion: "Federal contractor shield",
    onmywayFeatures: [
      "FCA / GovCon compliance",
      "Employee safety proof",
      "Federal contractor readiness",
    ],
    criteria: [
      "Complete federal contractor safety readiness assessment",
      "Maintain required workforce protection documentation",
      "Generate and retain GovCon compliance reports",
    ],
    issueDate: "Upon readiness validation",
    expirationDate: "12 months from issue date",
  }),
  company({
    id: "sec-investor-mitigation-reporting",
    bundleTypes: ["public"],
    title: "SEC Investor Mitigation Reporting",
    subtitle: "REPORTING / EMPLOYEE SAFETY COMPLIANCE",
    description:
      "Public company readiness for SEC investor mitigation reporting and employee safety compliance documentation.",
    recognitionText:
      "for achieving SEC Investor Mitigation Reporting readiness with documented employee safety compliance records.",
    themeColor: "#2563EB",
    emoji: "📋",
    iconSuggestion: "SEC reporting document",
    onmywayFeatures: [
      "SEC investor mitigation reporting",
      "Employee safety compliance",
      "Public company readiness",
    ],
    criteria: [
      "Establish SEC-aligned safety reporting program",
      "Maintain investor mitigation documentation",
      "Complete workforce safety compliance records",
    ],
    issueDate: "Upon program validation",
    expirationDate: "12 months from issue date",
  }),
];

export const PRIVATE_COMPANY_CERT_IDS = COMPANY_CERTIFICATIONS.filter((c) =>
  c.bundleTypes.includes("private")
).map((c) => c.id);

export const PUBLIC_COMPANY_CERT_IDS = COMPANY_CERTIFICATIONS.filter((c) =>
  c.bundleTypes.includes("public")
).map((c) => c.id);

export const GOVERNMENT_COMPANY_CERT_IDS = COMPANY_CERTIFICATIONS.filter((c) =>
  c.bundleTypes.includes("government_contractor")
).map((c) => c.id);

export const getCompanyCertsForBundleType = (
  bundleType: CompanyBundleType
): CertificationRecord[] =>
  COMPANY_CERTIFICATIONS.filter((c) => c.bundleTypes.includes(bundleType));
